import { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, Search, Trash2, AlertCircle, CheckCircle, Loader2, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminTools({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [coaches, setCoaches] = useState([]);
  const [loadingCoaches, setLoadingCoaches] = useState(false);
  const [showCoaches, setShowCoaches] = useState(false);

  useEffect(() => {
    loadCoaches();
  }, []);

  const loadCoaches = async () => {
    setLoadingCoaches(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/coach/all-coaches`);
      setCoaches(response.data.coaches);
    } catch (err) {
      console.error('Failed to load coaches:', err);
    } finally {
      setLoadingCoaches(false);
    }
  };

  const handleDeleteCoach = async (coachEmail) => {
    if (!window.confirm(`⚠️ DELETE COACH ACCOUNT?\n\nEmail: ${coachEmail}\n\nThis will permanently delete this coach account. All their clients will become orphaned.\n\nAre you SURE?`)) {
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/coach/delete-coach/${encodeURIComponent(coachEmail)}`);
      setSuccessMessage(`Coach account ${coachEmail} has been deleted!`);
      loadCoaches(); // Reload the list
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete coach');
    }
  };

  const handleSearch = async () => {
    if (!searchEmail.trim()) {
      setError('Please enter an email address');
      return;
    }

    setSearching(true);
    setError('');
    setSuccessMessage('');
    setSearchResult(null);

    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/coach/clients/search?email=${encodeURIComponent(searchEmail.trim())}`
      );
      setSearchResult(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError(`No client found with email: ${searchEmail}`);
      } else {
        setError(err.response?.data?.detail || 'Failed to search for client');
      }
    } finally {
      setSearching(false);
    }
  };

  const handleDelete = async () => {
    if (!searchResult?.client?.email) return;

    if (!window.confirm(`Are you sure you want to delete the account for ${searchResult.client.email}?\n\nThis will permanently remove:\n- The user account\n- All their data (meal plans, weight logs, messages, etc.)\n- Any active Stripe subscriptions\n\nThis cannot be undone!`)) {
      return;
    }

    setDeleting(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await axios.delete(
        `${BACKEND_URL}/api/coach/clients/by-email/${encodeURIComponent(searchResult.client.email)}`
      );
      setSuccessMessage(response.data.message);
      setSearchResult(null);
      setSearchEmail('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete client');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/coach')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Admin Tools</h1>
                <p className="text-sm text-gray-500">Find and manage client accounts</p>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Info Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">What This Tool Does</h3>
              <p className="text-sm text-blue-800">
                Search for client accounts, view all coaches in the system, and delete orphaned/phantom accounts.
              </p>
            </div>
          </div>
        </div>

        {/* Coach Management Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Users className="w-6 h-6 text-blue-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">All Coach Accounts</h2>
                <p className="text-sm text-gray-500">View and manage coach accounts in the system</p>
              </div>
            </div>
            <button
              onClick={() => setShowCoaches(!showCoaches)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {showCoaches ? 'Hide' : 'Show'} Coaches ({coaches.length})
            </button>
          </div>

          {showCoaches && (
            <div className="space-y-3 mt-4">
              {loadingCoaches ? (
                <div className="text-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-600" />
                </div>
              ) : coaches.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No coaches found</p>
              ) : (
                coaches.map((coach) => (
                  <div key={coach.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-gray-900">
                            {coach.first_name} {coach.last_name}
                            {coach.id === user.id && (
                              <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                YOU
                              </span>
                            )}
                          </h3>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">📧 {coach.email}</p>
                        <p className="text-xs text-gray-400 font-mono">ID: {coach.id}</p>
                        {coach.created_at && (
                          <p className="text-xs text-gray-500 mt-1">
                            Created: {new Date(coach.created_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      {coach.id !== user.id && (
                        <button
                          onClick={() => handleDeleteCoach(coach.email)}
                          className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors flex items-center gap-1"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Search Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Search for Client Account</h2>
          
          <div className="flex gap-3">
            <input
              type="email"
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Enter client email address..."
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              disabled={searching || deleting}
            />
            <button
              onClick={handleSearch}
              disabled={searching || deleting || !searchEmail.trim()}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {searching ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="w-5 h-5" />
                  Search
                </>
              )}
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-800">{successMessage}</p>
            </div>
          )}
        </div>

        {/* Search Result Card */}
        {searchResult && searchResult.found && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Account Found</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {searchResult.assigned_to_you 
                    ? '✓ This account is assigned to you' 
                    : '⚠️ This account is NOT assigned to you'}
                </p>
              </div>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete Account
                  </>
                )}
              </button>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Email</p>
                  <p className="text-sm font-medium text-gray-900">{searchResult.client.email}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Name</p>
                  <p className="text-sm font-medium text-gray-900">
                    {searchResult.client.first_name} {searchResult.client.last_name}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Account Type</p>
                  <p className="text-sm font-medium text-gray-900">
                    {searchResult.client.is_trial ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Trial Account
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Paid Account
                      </span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Created</p>
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(searchResult.client.created_at).toLocaleDateString()}
                  </p>
                </div>
                {searchResult.client.trial_expires_at && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Trial Expires</p>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(searchResult.client.trial_expires_at).toLocaleDateString()}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-gray-500 mb-1">Coach ID</p>
                  <p className="text-sm font-mono text-gray-600 truncate">{searchResult.client.coach_id || 'None'}</p>
                </div>
              </div>

              {!searchResult.assigned_to_you && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> This account is assigned to a different coach (or no coach at all). 
                    This is why it doesn't appear in your client list. You can still delete it if needed.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="mt-8 p-6 bg-gray-50 rounded-xl border border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-3">Common Use Cases:</h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>Someone signed up but their account doesn't appear in your client list</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>You're testing signup but get "email already registered" error</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>Need to remove a test account completely</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
